import React, { useMemo, useState } from "react";
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import CommCard from "src/common/CommCard";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import { TableLoader, TableNotFound } from "src/Utils/TableLoader";
import type { ISessionEnroll } from "src/Types/module/b2bSession/b2bSessionEnroll.types";
import CreateSessionEnrollModal from "./CreateSessionEnrollModal";
import SessionEnrollTableRow from "./SessionEnrollTableRow";

const SessionEnrollList: React.FC = () => {
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
	const [pagination, setPagination] = useState({
		limit: 50,
		skip: 0,
	});

	const { data, isLoading } = useGetQueryWithTotal<ISessionEnroll[]>({
		endPoint: "/api/fair/admin/sessions/enroll",
		queryKeys: ["SessionEnrolls", "SessionEnroll", statusFilter],
		params: {
			...(statusFilter && statusFilter !== "all"
				? { status: statusFilter }
				: {}),
		},
	});

	const enrollList: ISessionEnroll[] = useMemo(() => {
		if (Array.isArray(data?.data)) return data.data;
		if (Array.isArray(data)) return (data as unknown) as ISessionEnroll[];
		return [];
	}, [data]);

	// Filter by search query and status (in case API returns all)
	const filteredEnrolls = useMemo(() => {
		let list = enrollList;

		if (statusFilter && statusFilter !== "all") {
			list = list.filter(
				(item) => item.status?.toLowerCase() === statusFilter.toLowerCase()
			);
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			list = list.filter((item) => {
				const fullName = `${item.b2b_first_name || ""} ${item.b2b_last_name || ""
					}`.toLowerCase();
				const org = (item.b2b_organization || "").toLowerCase();
				const email = (item.b2b_email || "").toLowerCase();
				const phone = (item.b2b_phone || "").toLowerCase();
				const sessionNo = (item.session_no || "").toLowerCase();
				const invoiceNo = (item.invoice_no || "").toLowerCase();
				const date = (item.date || "").toLowerCase();

				return (
					fullName.includes(query) ||
					org.includes(query) ||
					email.includes(query) ||
					phone.includes(query) ||
					sessionNo.includes(query) ||
					invoiceNo.includes(query) ||
					date.includes(query) ||
					String(item.id).includes(query) ||
					String(item.b2b_id).includes(query)
				);
			});
		}

		return list;
	}, [enrollList, statusFilter, searchQuery]);

	// Paginated slice
	const paginatedData = useMemo(() => {
		return filteredEnrolls.slice(
			pagination.skip,
			pagination.skip + pagination.limit
		);
	}, [filteredEnrolls, pagination.skip, pagination.limit]);

	return (
		<CommCard
			showCardHeader
			headerTitle={`Session Enrollment List (${filteredEnrolls.length})`}
			helmetTitle="BTTF - B2B Session Enrollment List"
			showSearch
			searchPlaceHolder="Search by member name, organization, session, invoice..."
			handleSearchFun={(query) => {
				setSearchQuery(query);
				setPagination((prev) => ({ ...prev, skip: 0 }));
			}}
			showStatus
			statusLabel="Status"
			statusOptions={[
				{ id: "all", name: "All" },
				{ id: "approved", name: "Approved" },
				{ id: "pending", name: "Pending" },
				{ id: "rejected", name: "Rejected" },
				{ id: "completed", name: "Completed" },
			]}
			handleSelectStatus={(selectedStatus) => {
				setStatusFilter(selectedStatus);
				setPagination((prev) => ({ ...prev, skip: 0 }));
			}}
			showButton
			btnText="Enroll Session"
			handleBtnForModalFun={() => setOpenCreateModal(true)}
		>
			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>SL.</TableCell>
							<TableCell>B2B Details</TableCell>
							<TableCell>Session Slot</TableCell>
							<TableCell>Invoice Details</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Created Date</TableCell>
							<TableCell align="right">Action</TableCell>
						</TableRow>
					</TableHead>

					{isLoading ? (
						<TableLoader />
					) : paginatedData.length > 0 ? (
						<TableBody>
							{paginatedData.map((enroll, index) => (
								<SessionEnrollTableRow
									key={enroll.id}
									enroll={enroll}
									serial={pagination.skip + index + 1}
								/>
							))}
						</TableBody>
					) : (
						<TableNotFound />
					)}
				</Table>
			</TableContainer>

			{filteredEnrolls.length > 0 && (
				<Box p={2}>
					<CommonPagination
						total={filteredEnrolls.length}
						dataShowPerPage={pagination.limit}
						paginationCalFun={(limit: number, skip: number) => {
							setPagination({ limit, skip });
						}}
					/>
				</Box>
			)}

			{/* Create Session Enrollment Modal */}
			<CreateSessionEnrollModal
				open={openCreateModal}
				handleClose={() => setOpenCreateModal(false)}
			/>
		</CommCard>
	);
};

export default SessionEnrollList;
